import { useEffect } from 'react';
import useSession from '@/hooks/context/useSession';
import { isLogRocketEnabled } from '@/utils/logrocket.utils';
import Logger from '@/lib/logger';

/**
 * Hook to identify the user in LogRocket based on the current session.
 *
 * Rules:
 * 1. If the user has support access, identify using their email.
 * 2. If an authenticated member_id exists, identify using the member_id.
 * 3. Fallback to using the session_id for anonymous identification.
 */
export default function useLogRocketIdentify() {
  const {
    session: { member_id, session_id },
    userEmail,
    hasSupportAccess,
    roles,
  } = useSession();

  useEffect(() => {
    let identity: string | null = null;
    let traits: Record<string, string | number | boolean> = {};

    if (hasSupportAccess && userEmail) {
      identity = userEmail;
      traits = {
        email: userEmail,
        hasSupportAccess: true,
        roles: roles.join(','),
      };
      // this would be for a future update. once @jake confirms we can
      // } else if (member_id) {
      //   identity = member_id;
      //   traits = {
      //     member_id,
      //   };
    } else if (session_id) {
      identity = session_id;
    }

    if (identity && isLogRocketEnabled) {
      import('logrocket').then(({ default: LogRocket }) => {
        try {
          Logger.debug('Identifying user on LogRocket', { identity, traits });
          LogRocket.identify(identity, traits);
        } catch (e) {
          Logger.error("Wasn't able to identify user on LogRocket", e);
        }
      });
    }
  }, [member_id, session_id, userEmail, hasSupportAccess, roles]);
}
